import type { Client, PortfolioSource } from "creso-core/types";
import { Button, cn } from "web-design-system";
import {
  useMfCentralImportState,
  useMfCentralStatus,
  usePortfolioCategorySummary,
  usePortfolioSummaryV2,
  useSchemeFolioHoldingsData,
} from "creso-core/hooks";
import { RefreshCwIcon } from "lucide-react";
import { format } from "date-fns";
import { mfCentralRefetchInterval } from "creso-core/constants";

/**
 * Shows when external holdings were last synced and lets the host reopen its
 * MF Central import flow. Also warms the portfolio queries once an import
 * lands, so the tables behind it pick up the new holdings.
 */
export function PortfolioImportStatus({
  selectedTab,
  client,
  onImportHoldings,
}: {
  selectedTab: PortfolioSource;
  client: Client;
  onImportHoldings?: () => void;
}) {
  const investmentAccount = client?.investment_account ?? "";

  const { data: importStatus } = useMfCentralStatus(
    investmentAccount,
    !!investmentAccount,
    mfCentralRefetchInterval
  );

  const importState = useMfCentralImportState(importStatus);

  usePortfolioSummaryV2(investmentAccount, importStatus?.status === "SUCCESS");

  useSchemeFolioHoldingsData(
    { investmentAccountGuid: investmentAccount },
    {
      enabled: importStatus?.status === "SUCCESS" && !!investmentAccount,
    }
  );

  usePortfolioCategorySummary(
    { investmentAccountId: investmentAccount },
    {
      enabled: importStatus?.status === "SUCCESS" && !!investmentAccount,
    }
  );

  if (selectedTab === "INTERNAL" || !importStatus?.last_fetch_at) {
    return null;
  }

  return (
    <div className="flex flex-row gap-3 items-center">
      <span className="text-xs text-muted-foreground">
        {importState === "in-progress"
          ? "Sync in progress..."
          : importState === "expired"
            ? "Taking longer than usual."
            : importStatus?.last_fetch_at
              ? selectedTab === "OVERALL"
                ? `External holdings last updated on ${format(
                    new Date(importStatus?.last_fetch_at),
                    "MMM d, yyyy"
                  )}`
                : `Last updated on ${format(
                    new Date(importStatus?.last_fetch_at),
                    "MMM d, yyyy"
                  )}`
              : null}
      </span>
      {onImportHoldings && (
        <Button
          disabled={importState !== "idle"}
          size="icon"
          variant="outline"
          onClick={onImportHoldings}
        >
          <RefreshCwIcon
            className={cn("size-4", {
              "animate-spin": importState !== "idle",
            })}
          />
        </Button>
      )}
    </div>
  );
}
